
import { ConnectionListings } from '@graphql-box/react';
import { type Review, ScreenType } from '@tmdb-graphql-api/schema/types';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Ancestor } from '#enums/ancestors.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { SlideoutDrawer } from '../SlideoutDrawer/index.tsx';
import { Component } from './Component.tsx';
import { formatReviews } from './helpers/formatReviews.ts';
import { type QueryProps } from './types.ts';

const queries = {
  [ScreenType.Movie]: "query GetMovieReviews(\n  $after: String\n  $before: String\n  $first: Int = 10\n  $id: ID!\n  $last: Int\n) {\n  movie(id: $id) {\n    reviews(\n      after: $after,\n      before: $before,\n      first: $first,\n      last: $last,\n    ) {\n      edges {\n        cursor\n        node {\n          author\n          authorDetails {\n            username\n            avatarPath\n            rating\n          }\n          content\n          createdAt\n          mediaTitle\n        }\n      }\n      pageInfo {\n        hasNextPage\n        hasPreviousPage\n        startCursor\n        endCursor\n      }\n      totalCount\n    }\n  }\n}\n",
  [ScreenType.Tv]: "query GetTvReviews(\n  $after: String\n  $before: String\n  $first: Int = 10\n  $id: ID!\n  $last: Int\n) {\n  tv(id: $id) {\n    reviews(\n      after: $after,\n      before: $before,\n      first: $first,\n      last: $last,\n    ) {\n      edges {\n        cursor\n        node {\n          author\n          authorDetails {\n            username\n            avatarPath\n            rating\n          }\n          content\n          createdAt\n          mediaTitle\n        }\n      }\n      pageInfo {\n        hasNextPage\n        hasPreviousPage\n        startCursor\n        endCursor\n      }\n      totalCount\n    }\n  }\n}\n",
};

const requestPaths = {
  [ScreenType.Movie]: 'movie.reviews',
  [ScreenType.Tv]: 'tv.reviews',
};

export const ReviewsDrawerQuery = ({ id, setActiveReviewsDrawer, title, type }: QueryProps) => {
  const query = queries[type];
  const { config } = useToolbelt();
  const [open, setOpen] = useState<boolean>(true);
  const intersectionRootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setActiveReviewsDrawer(undefined);
      }, 310);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const memoReviewsQuery = useMemo(
    () => (
      <ConnectionListings<Review>
        componentName="ReviewsDrawer"
        intersectionRoot={intersectionRootRef.current}
        query={query}
        requestPath={requestPaths[type]}
        resultsPerRequest={10}
        variables={{ id }}
      >
        {({ listings, ...rest }) => (
          // Added because Next.js requires components that use useSearchParams hook
          // to be wrapped in Suspense: https://nextjs.org/docs/messages/missing-suspense-with-csr-bailout
          <Suspense>
            <Component items={formatReviews(listings, config)} mediaTypeId={id} {...rest} />
          </Suspense>
        )}
      </ConnectionListings>
    ),
    [id], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <SlideoutDrawer
      ancestor={Ancestor.ReviewsBadge}
      open={open}
      setOpen={setOpen}
      title={title ? `Reviews for ${title}` : 'Reviews'}
    >
      {memoReviewsQuery}
    </SlideoutDrawer>
  );
};
